import {
  interactionDiagnosticCopy,
  normalizeInteractionDiagnostics,
  scenarioCoverage,
  scenarioFailureDetail,
  replayTriggerNote,
  replayReasonText,
  readerReason,
  scenarioHumanLabel
} from '../../utils/inpInteractionDiagnostics';

const Timing = ({ value, missing }) => (
  <span className={value == null ? 'text-slate-500' : 'text-slate-200'}>
    {value == null ? missing : `${Math.round(value)} ms`}
  </span>
);

const INPInteractionDiagnostics = ({ diagnostics: rawDiagnostics, language = 'en' }) => {
  const diagnostics = normalizeInteractionDiagnostics(rawDiagnostics);
  if (!diagnostics) return null;
  const copy = interactionDiagnosticCopy(language);
  const { profile } = diagnostics;

  return (
    <section data-testid="inp-interaction-diagnostics" className="rounded-lg border border-slate-700 bg-slate-800/40 p-3 space-y-3">
      <div>
        <div className="text-xs uppercase tracking-wider text-cyan-400">{copy.title}</div>
        <p className="mt-1 text-[11px] text-slate-400">{copy.intro}</p>
        <p data-scenario-coverage className="mt-2 text-xs text-slate-200">{copy.scenarioCoverage(scenarioCoverage(diagnostics))}</p>
        {diagnostics.pageUrl && <p className="mt-1 text-[11px] text-slate-500 break-all"><span className="font-medium">{copy.page}:</span> {diagnostics.pageUrl}</p>}
      </div>
      {diagnostics.coverage.length > 0 && (
        <div>
          <div className="text-xs font-medium text-slate-300">{copy.coverageTitle}</div>
          <p className="mt-1 text-[11px] text-slate-500">{copy.coverageIntro}</p>
          <div className="mt-2 overflow-x-auto">
            <table className="w-full min-w-[560px] text-[11px]">
              <thead className="text-left text-slate-500">
                <tr>{[copy.scenario, copy.result, copy.phase, copy.attempts, copy.duration].map(label => <th key={label} className="px-2 py-1 font-medium">{label}</th>)}</tr>
              </thead>
              <tbody>{diagnostics.coverage.map(item => (
                <tr key={item.id} className="border-t border-slate-700/70">
                  <td className="px-2 py-2 text-slate-200">{scenarioHumanLabel(item, language)}</td>
                  <td className="px-2 py-2">
                    <span className={item.status === 'measured' ? 'text-green-400' : item.status === 'failed' ? 'text-red-400' : 'text-amber-400'}>{copy.status[item.status]}</span>
                    {item.reason && <span className="block text-slate-500">{readerReason(item.reason, language)}</span>}
                  </td>
                  <td className="px-2 py-2 text-slate-400">{item.phase ? copy.phaseLabel[item.phase] : copy.missingDetail}</td>
                  <td className="px-2 py-2 text-slate-400">{item.attempts ?? copy.missingDetail}</td>
                  <td className="px-2 py-2"><Timing value={item.durationMs} missing={copy.missing} /></td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[800px] text-[11px]">
          <thead className="text-left text-slate-500">
            <tr>{[copy.scenario, copy.result, copy.phase, copy.attempts, copy.duration, copy.input, copy.processing, copy.presentation].map(label => <th key={label} className="px-2 py-1 font-medium">{label}</th>)}</tr>
          </thead>
          <tbody>
            {diagnostics.scenarios.map(scenario => (
              <tr key={scenario.id} className="border-t border-slate-700/70">
                <td className="px-2 py-2 text-slate-200">{scenarioHumanLabel(scenario, language)}</td>
                <td className="px-2 py-2">
                  <span className={scenario.status === 'failed' ? 'text-red-400' : scenario.status === 'measured' ? 'text-green-400' : 'text-amber-400'}>{copy.status[scenario.status]}</span>
                  {scenario.reason && !scenario.executionDiagnostics?.error?.classification && <span className="block text-slate-500">{readerReason(scenario.reason, language)}</span>}
                  {scenarioFailureDetail(scenario, language) && <span className="block text-slate-500">{scenarioFailureDetail(scenario, language)}</span>}
                </td>
                <td className="px-2 py-2 text-slate-400">{scenario.phase ? copy.phaseLabel[scenario.phase] : copy.missing}</td>
                <td className="px-2 py-2 text-slate-400">{scenario.attempts ?? copy.missing}</td>
                {[scenario.durationMs, scenario.inputDelayMs, scenario.processingMs, scenario.presentationMs].map((value, index) => <td key={index} className="px-2 py-2"><Timing value={value} missing={copy.missing} /></td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="border-t border-slate-700 pt-3 space-y-2">
        <div className="text-xs font-medium text-purple-300">{copy.traceTitle}</div>
        <p className="text-[11px] text-slate-400">{copy.traceDisclaimer}</p>
        <p className="text-xs text-slate-300">
          <span className="font-medium">{copy.profileStatus[profile.status]}</span>
          {profile.reason && <> — {replayReasonText(profile, language)}</>}
        </p>
        {replayTriggerNote(profile, language) && <p className="text-xs text-slate-400">{replayTriggerNote(profile, language)}</p>}
        <p className="text-[11px] text-slate-400">
          {(profile.scenarioLabel || profile.scenarioId) && <><span className="font-medium">{copy.replayScenario}:</span> {profile.scenarioLabel || profile.scenarioId} · </>}
          <span className="font-medium">{copy.replayDuration}:</span> <Timing value={profile.durationMs} missing={copy.missing} /> ·{' '}
          <span className="font-medium">{copy.evidence}:</span> {copy.evidenceLevel[profile.evidenceLevel]}
        </p>
        {profile.summary && (
          <div className="text-[11px] text-slate-400">
            <span className="font-medium">{copy.categories}:</span>{' '}
            {copy.scripting} <Timing value={profile.summary.scriptingMs} missing={copy.missing} /> ·{' '}
            {copy.layout} <Timing value={profile.summary.layoutMs} missing={copy.missing} /> ·{' '}
            {copy.paint} <Timing value={profile.summary.paintMs} missing={copy.missing} />
          </div>
        )}
        {profile.scripts.length > 0 && (
          <div className="text-[11px] text-slate-400">
            <span className="font-medium">{copy.candidates}</span> — {copy.candidateWarning}
            <ul className="mt-1 space-y-1">
              {profile.scripts.map((script, index) => (
                <li key={`${script.url}-${index}`} className="break-all">
                  <code className="text-cyan-300">{script.url || copy.unsupportedSource}</code>
                  {script.functionName && <> · <code className="text-slate-300">{script.functionName}</code></>}
                  {script.durationMs != null && <> · <Timing value={script.durationMs} missing={copy.missing} /></>}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
};

export default INPInteractionDiagnostics;